import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImg } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { CITIES, DISCIPLINES } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Camera, Loader2, Save } from "lucide-react";

const field = "bg-[#0d0d0d] border-[#27272a] text-white h-11 focus:ring-2 focus:ring-volt focus:border-volt";

export default function TrainerProfilePanel({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (open && user) {
      setForm({
        fighter_name: user.name || user.fighter_name || "",
        gym_name: user.gym_name || "",
        city: user.city || "",
        discipline: user.discipline || "",
        bio: user.bio || "",
        avatar_url: user.avatar_url || null,
      });
    }
  }, [open, user]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const prepared = await compressImage(file);
      const fd = new FormData();
      fd.append("file", prepared);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, avatar_url: data.url }));
      toast.success("Photo uploaded. Hit save to keep it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/profile", form);
      setUser(data.user);
      toast.success("Profile updated.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trainer-profile-panel" className="bg-[#161616] border-[#27272a] text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl uppercase tracking-tight">Trainer Profile</DialogTitle>
          <DialogDescription className="text-zinc-400">Update your photo, gym and bio.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl overflow-hidden border border-[#27272a] bg-[#0d0d0d] flex items-center justify-center shrink-0">
            {form.avatar_url ? <img src={resolveImg(form.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
              : <span className="font-heading text-4xl text-zinc-700">{form.fighter_name?.[0] || "?"}</span>}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} data-testid="trainer-avatar-input" />
            <button
              data-testid="trainer-avatar-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 border border-[#27272a] text-white text-sm uppercase tracking-wider px-4 py-2 rounded-xl hover:border-volt hover:text-volt transition-colors duration-200 disabled:opacity-50"
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              {uploading ? "Uploading…" : "Change Photo"}
            </button>
            <p className="text-xs text-zinc-600 mt-1.5">JPG, PNG, WEBP or HEIC · max 15MB</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Trainer Name</Label>
            <Input data-testid="trainer-profile-name" value={form.fighter_name} onChange={(e) => set("fighter_name")(e.target.value)} className={field} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Gym</Label>
            <Input data-testid="trainer-profile-gym" value={form.gym_name} onChange={(e) => set("gym_name")(e.target.value)} className={field} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">City / Region</Label>
            <Select value={form.city} onValueChange={set("city")}>
              <SelectTrigger data-testid="trainer-profile-city" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161616] border-[#27272a] text-white">
                {CITIES.map((o) => <SelectItem key={o} value={o} className="focus:bg-volt focus:text-black">{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Primary Discipline</Label>
            <Select value={form.discipline} onValueChange={set("discipline")}>
              <SelectTrigger data-testid="trainer-profile-discipline" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161616] border-[#27272a] text-white">
                {DISCIPLINES.map((o) => <SelectItem key={o} value={o} className="focus:bg-volt focus:text-black">{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-zinc-400">Bio</Label>
          <Textarea data-testid="trainer-profile-bio" value={form.bio} onChange={(e) => set("bio")(e.target.value)} placeholder="Coaching background, achievements, fighters you've cornered…" className="bg-[#0d0d0d] border-[#27272a] text-white focus:ring-2 focus:ring-volt resize-none" />
        </div>

        <button
          data-testid="trainer-profile-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full bg-volt text-black font-heading font-bold uppercase tracking-widest py-3 rounded-xl transition-colors duration-200 hover:bg-volt-hover disabled:opacity-40 flex items-center justify-center gap-2 mt-2"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
